import { createResource, type Accessor } from 'solid-js'
import { safeParse } from 'valibot'
import { backendAxios } from '../../axios'
import type {
  IndexMapQuery,
  MapRowValue,
  StateRowTransportCollection,
} from '../../backend.d'
import {
  IndexMapQuerySchema,
} from '../../backend_schemas'
import { getStateDiskByType } from '../../lib/core/disk/stateDiskResolver'
import { hasIndexedDB } from '../../lib/core/Storage/IndexedDB/hasIndexedDB'
import { isOfflineOnly } from '../../lib/core/isOfflineOnly'
import {
  EXPORT_DISK_PLACEHOLDER_ERROR,
  StateMapDisk,
} from '../../lib/core/state/state_map_disk.enum'
import { StateType } from '../../lib/core/state/state_type.enum'
import { listWorkspaceMapRows } from '../../lib/core/workspace/workspaceMapsClient'
import { decodePersistedStateData } from '../../lib/core/state/persisted_state'

export interface ExtendedIndexMapQuery extends IndexMapQuery {
  disk?: StateMapDisk
}

export type MapCollectionRowValue = MapRowValue & {
  file_id?: string
}

async function fetchWorkspaceMapCollectionByQuery(
  inputParams: ExtendedIndexMapQuery
): Promise<MapCollectionRowValue[]> {
  const result = safeParse(IndexMapQuerySchema, inputParams)
  if (!result.success) {
    return []
  }
  const params = result.output

  const collection = await listWorkspaceMapRows()
  const filteredCollection =
    params.owner_profile_id == null
      ? collection
      : collection.filter((row) => row.creator.id === params.owner_profile_id || row.owner.id === params.owner_profile_id)

  return filteredCollection
}

export async function fetchMapCollectionByQuery(inputParams: ExtendedIndexMapQuery): Promise<MapCollectionRowValue[]> {
  const disk = inputParams.disk
  const result = safeParse(IndexMapQuerySchema, inputParams)

  if (result.success) {
    if (!disk) {
      throw new Error('Missing disk')
    }
    const params = result.output
    let collection: StateRowTransportCollection = []

    if (disk === StateMapDisk.IndexedDB) {
      if (!hasIndexedDB()) {
        throw new Error('IndexedDB requested but not available')
      }
      collection = await getStateDiskByType(disk, StateType.Map).list()
    } else if (disk === StateMapDisk.Memory) {
      collection = await getStateDiskByType(disk, StateType.Map).list()
    } else if (disk === StateMapDisk.Cloud && !isOfflineOnly && import.meta.env.VITE_HONO_URL) {
      try {
        const response = await backendAxios.get(`/st1/map`, { params })
        collection = response.data as StateRowTransportCollection
      } catch (e) {
        console.error('Failed to fetch maps from cloud', e)
      }
    } else if (disk === StateMapDisk.Workspace) {
      return await fetchWorkspaceMapCollectionByQuery(inputParams)
    } else if (disk === StateMapDisk.Export) {
      throw new Error(EXPORT_DISK_PLACEHOLDER_ERROR)
    } else {
      throw new Error('Invalid disk: ' + disk)
    }

    return collection.map((row) => ({
      ...row,
      data: decodePersistedStateData(row.data, disk === StateMapDisk.Memory ? StateMapDisk.Memory : StateMapDisk.IndexedDB),
    })) as MapCollectionRowValue[]
  }
  return []
}

export default function createMapCollectionResource(
  getInputQuery: Accessor<Partial<ExtendedIndexMapQuery>>
) {
  const resource = createResource(getInputQuery, fetchMapCollectionByQuery)
  return resource
}
